import React, { useEffect, useState } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';

// Chart.js 플러그인 등록
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

class Params {
    indexBy: string = ''; // y축 값으로 사용할 key (수평이므로 y축이 라벨)
    xLabel: string = ''; // x축 라벨
    yLabel: string = ''; // y축 라벨
    colors: string[] = []; // 데이터의 색상
    valueFormat: Function = (value: any) => value; // 툴팁 값 형식 지정
    labelFormat: Function = (value: any) => value; // 라벨 형식 지정
    valueType: string = ''; // 값 유형
    keys: string[] = []; // 표시할 데이터 키
    data: any = { labels: [], datasets: [] };
}

function ChartHorizontalBar(props: any) {
    const [params, setParams] = useState(new Params());

    useEffect(() => {
        const initParams = new Params();

        initParams.indexBy = props.indexBy || '';
        initParams.xLabel = props.xLabel || '';
        initParams.yLabel = props.yLabel || '';
        initParams.colors = props.colors || generateColors(props.keys || []); // colors가 없으면 자동 생성

        if (props.labelFormat) {
            initParams.labelFormat = props.labelFormat;
        }
        if (props.valueFormat) {
            initParams.valueFormat = props.valueFormat;
        }
        if (props.valueType) {
            initParams.valueType = props.valueType;
        }

        const data = props.data || [];
        const keys = props.keys || []; // 표시할 데이터 키들

        // Chart.js의 데이터 형식으로 변환 (수평 막대용)
        initParams.data = {
            labels: data.map((item: any) => item[initParams.indexBy]), // y축 레이블 생성
            datasets: keys.map((key: string, index: number) => ({
                label: props.labelFormat ? props.labelFormat(key) : key, // 각 데이터 집합의 라벨
                data: data.map((item: any) => item[key]), // 각 데이터 키에 해당하는 값
                backgroundColor: initParams.colors[index],
                borderColor: initParams.colors[index],
                borderWidth: 1, // 막대 테두리 두께
            })),
        };

        setParams(() => ({
            ...initParams,
        }));
    }, [props]);

    // 기본 색상 팔레트
    const defaultColors = [
        'rgba(75, 192, 192, 1)', // 청록색
        'rgba(255, 99, 132, 1)', // 빨간색
        'rgba(54, 162, 235, 1)', // 파란색
        'rgba(255, 206, 86, 1)', // 노란색
        'rgba(153, 102, 255, 1)', // 보라색
        'rgba(255, 159, 64, 1)', // 주황색
    ];

    // 색상 자동 생성 함수 (props.colors 없을 경우 기본 색상 사용)
    const generateColors = (keys: string[]) => {
        return keys.map((_, index) => defaultColors[index % defaultColors.length]);
    };

    // 차트 옵션 (수평 설정 + 데이터 라벨 항상 표시)
    const options = {
        indexAxis: 'y' as const, // 수평 막대를 위한 설정
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: {
                right: 60  // 오른쪽 여백 추가
            }
        },
        plugins: {
            legend: {
                display: false, // 범례 숨김
            },
            tooltip: {
                mode: 'index' as const,
                intersect: false,
                callbacks: {
                    label: (context: any) => {
                        const value = context.raw;
                        return ` ${context.dataset.label}: ${params.valueFormat(value)}`;
                    },
                },
            },
            datalabels: {
                display: true,
                anchor: 'end' as const,
                align: 'end' as const,  // 'right'에서 'end'로 변경
                offset: 4,  // 막대 끝에서 약간 떨어진 위치
                color: 'white',
                font: {
                    size: 12,
                    weight: 'bold' as const,
                },
                formatter: (value: any) => {
                    return params.valueFormat(value);
                },
                clip: false  // 차트 영역 밖에도 라벨 표시 허용
            },
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: params.xLabel,
                    color: 'white',
                },
                beginAtZero: true,
                ticks: {
                    color: 'white',
                },
                grid: {
                    color: 'rgba(255, 255, 255, 0.1)',
                },
            },
            y: {
                title: {
                    display: true,
                    text: params.yLabel,
                    color: 'white',
                },
                ticks: {
                    color: 'white',
                },
                grid: {
                    color: 'rgba(255, 255, 255, 0.1)',
                },
            },
        },
    };

    return <Bar data={params.data} options={options} />;
}

export default ChartHorizontalBar;